/**
 * 微信登录按钮组件
 * 主入口组件，根据设备类型决定登录方式
 */

import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useWechatLogin, type WechatLoginError } from "@/hooks/useWechatLogin";
import WechatQRCodeDialog from "./WechatQRCodeDialog";
import { Loader2 } from "lucide-react";

export interface WechatLoginButtonProps {
  /** 自定义类名 */
  className?: string;
  /** 登录开始回调 */
  onLoginStart?: () => void;
  /** 登录错误回调 */
  onLoginError?: (error: WechatLoginError) => void;
  /** 登录成功后重定向路径 */
  redirectTo?: string;
}

export default function WechatLoginButton({
  className,
  onLoginStart,
  onLoginError,
  redirectTo = "/",
}: WechatLoginButtonProps) {
  const { t } = useTranslation("login");
  const [isProcessing, setIsProcessing] = useState(false);

  // 错误处理
  const handleError = useCallback(
    (error: WechatLoginError) => {
      setIsProcessing(false);
      // 显示 toast 通知
      const messages: Record<string, string> = {
        SDK_LOAD_FAILED: t(
          "domestic.wechatUnavailable",
          "微信登录暂时不可用，请稍后重试",
        ),
        CONFIG_MISSING: t("domestic.wechatConfigError", "微信登录配置错误"),
        NETWORK_ERROR: t(
          "domestic.wechatNetworkError",
          "网络连接失败，请检查网络后重试",
        ),
        TIMEOUT: t("domestic.wechatTimeout", "加载超时，请点击刷新重试"),
      };

      toast.error(messages[error.type] || error.message);

      // 调用外部错误回调
      onLoginError?.(error);

      // 记录日志
      console.error("[WechatLogin]", error.type, error.message);
    },
    [t, onLoginError],
  );

  // 使用微信登录 Hook
  const {
    sdkStatus,
    isConfigValid,
    isMobile,
    isWeChat,
    dialogOpen,
    setDialogOpen,
    handleLogin,
    preloadSdk,
    refreshQRCode,
    config,
  } = useWechatLogin({
    onError: handleError,
    autoPreload: true,
    redirectTo,
  });

  // 处理按钮点击
  const handleClick = useCallback(() => {
    if (isProcessing) return;

    onLoginStart?.();

    // 如果是移动端或微信浏览器，将发生重定向，保持处理状态
    if (isMobile || isWeChat) {
      setIsProcessing(true);
    }

    handleLogin();
  }, [isProcessing, isMobile, isWeChat, onLoginStart, handleLogin]);

  // 配置无效时禁用按钮
  if (!isConfigValid) {
    return (
      <Button
        type="button"
        className={`w-full bg-[#07c160] hover:bg-[#06ae56] text-white opacity-50 cursor-not-allowed ${className || ""}`}
        disabled
      >
        <WechatIcon className="mr-2 h-4 w-4" />
        {t("domestic.wechatLogin", "微信扫码登录")}
      </Button>
    );
  }

  return (
    <>
      {/* 微信登录按钮 */}
      <Button
        type="button"
        className={`w-full bg-[#07c160] hover:bg-[#06ae56] text-white ${className || ""}`}
        onClick={handleClick}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <WechatIcon className="mr-2 h-4 w-4" />
        )}
        {isProcessing && (isMobile || isWeChat)
          ? t("domestic.redirecting", "正在跳转...")
          : t("domestic.wechatLogin", "微信扫码登录")}
      </Button>

      {/* 二维码弹窗（仅桌面端使用） */}
      <WechatQRCodeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        config={config}
        sdkStatus={sdkStatus}
        preloadSdk={preloadSdk}
        onRefresh={refreshQRCode}
        onError={handleError}
      />
    </>
  );
}

/** 微信图标组件 */
function WechatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.269-.03-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" />
    </svg>
  );
}
